/**
 * THE ONLY PLACE IN THE CODEBASE WHERE A TUNABLE NUMBER MAY LIVE.
 *
 * If you find a balance number anywhere else, it is in the wrong file. That
 * includes CSS: sizes that the game logic also needs are declared here and
 * pushed into custom properties at runtime (see client/src/game/GameArea.ts).
 *
 * Gameplay, lifecycle, input and animation values are centralized here so a
 * balance pass never requires hunting through render or server code.
 */

export const BALANCE = {
  // --------------------------------------------------------------------------
  // Clock synchronisation. Infrastructure, not gameplay — but tunable, so here.
  // --------------------------------------------------------------------------
  clock: {
    /** Pings fired during the initial handshake before the clock is trusted. */
    handshakeSamples: 5,
    /** Delay between handshake pings (ms). */
    handshakeIntervalMs: 120,
    /** Steady-state re-sync interval once synced (ms). */
    resyncIntervalMs: 10_000,
    /** Keep the N lowest-RTT samples; high-RTT ones carry the most offset error. */
    keepBestSamples: 5,
    /** Above this RTT a sample is discarded outright (ms). */
    maxUsableRttMs: 800,
    /** Offset jumps smaller than this are slewed, not snapped (ms). */
    slewThresholdMs: 40,
  },

  // --------------------------------------------------------------------------
  // Match lifecycle. Every one of these is a *duration*; the wire always carries
  // the resulting absolute timestamp, never the duration itself.
  // --------------------------------------------------------------------------
  phases: {
    /** How long the room waits for a second player before giving up (ms). */
    matchmakingTimeoutMs: 30_000,
    /** Ready substate inside `lobby` → `countdown` asset-confirmation grace (ms). */
    readyTimeoutMs: 5_000,
    /** Synchronised 3-2-1 before inputs open (ms). */
    countdownMs: 4_000,
    /** Initial LISTO beat before the visible 3-2-1 sequence (ms). */
    countdownReadyHoldMs: 1_000,
    /** Inputs arriving inside this window are queued for `startAt`, not rejected. */
    preStartInputGraceMs: 300,
    /** Length of a competitive versus round (ms). */
    versusRoundMs: 60_000,
    /** Length of the score-attack solo mode (ms). */
    scoreAttackRoundMs: 120_000,
    /**
     * How long the GO! flash stays up after `playing` begins (ms).
     *
     * Measured from the phase's authoritative `startsAt`, never from a local
     * timer: a client that backgrounds the tab mid-flash would otherwise come
     * back to a GO! frozen over a match that started seconds ago.
     */
    goHoldMs: 500,
    /** `ko` hold so the last animation lands before the result screen (ms). */
    resolveHoldMs: 5_000,
    /** A disconnected player has this long to come back before forfeiting (ms). */
    reconnectionGraceMs: 15_000,
  },

  // --------------------------------------------------------------------------
  // 2048 board.
  // --------------------------------------------------------------------------
  board: {
    size: 4,
    initialTiles: 2,
    spawnValue2Chance: 0.9,
    stalledTilesRemoved: 4,
  },

  // --------------------------------------------------------------------------
  // Classic 2048 scoring: award the value of every resulting merged tile.
  // --------------------------------------------------------------------------
  scoring: {
    pointsPerMergedValue: 1,
  },

  // --------------------------------------------------------------------------
  // Versus interaction.
  // --------------------------------------------------------------------------
  versus: {
    startingHp: 100,
    stalledBoardHpPenalty: 10,
    multiMergeBonusPerExtraMerge: 0.25,
    powerTileComboBonus: 1,
    powerTiles: [512, 1024, 2048] as readonly number[],
    damageByTile: {
      2: 0,
      4: 0,
      8: 1,
      16: 2,
      32: 3,
      64: 5,
      128: 8,
      256: 12,
      512: 18,
      1024: 28,
      2048: 45,
    } as Readonly<Record<number, number>>,
  },

  // --------------------------------------------------------------------------
  // Elo rating.
  // --------------------------------------------------------------------------
  rating: {
    /** Where a player who has never been rated starts. */
    initialRating: 1000,
    initialKFactor: 32,
    establishedKFactor: 16,
    establishedAfterMatches: 30,
    /**
     * Ratings cannot fall below this. Without a floor a losing streak walks a
     * player down indefinitely and matchmaking can no longer find them a game.
     */
    floor: 100,
    /** Logistic width. 400 means "+400 rating = 10x more likely to win". */
    scale: 400,
    /** Rating gap that makes a win an upset, for the secret achievement. */
    upsetRatingGap: 300,
  },

  account: {
    nicknameMaxLength: 10,
    usernameMinLength: 3,
    /**
     * Narrower than the 24 the `accounts` CHECK permits, and equal to
     * `nicknameMaxLength` on purpose: registration sets the player's display
     * name from this field, and that name has to fit a ranking row.
     */
    usernameMaxLength: 10,
    emailMaxLength: 254,
    passwordMinLength: 12,
    passwordMaxLength: 128,

    /**
     * The name a guest is given when they press JUGAR COMO INVITADO.
     *
     * Prefix plus suffix must fit `nicknameMaxLength` exactly: that is the
     * width `PlayerPanel` truncates at, and a stored name longer than what the
     * HUD can draw would read as one player on the board and another on the
     * profile screen. Seven characters of prefix therefore leave three.
     *
     * Guest names are not unique and are not meant to be — `device_id` is the
     * key, and the unique identifier is the account username, which only exists
     * once somebody registers.
     */
    guestNamePrefix: 'Player-',
    guestNameSuffixLength: 3,
    /** Lowercase twin of `privateCodeAlphabet`: nothing that misreads on a CRT. */
    guestNameAlphabet: '23456789abcdefghjkmnpqrstuvwxyz',

    /** How long a session lives when the player did not ask to be remembered. */
    sessionTtlMs: 12 * 60 * 60 * 1000,
    /** "Recordarme": long enough that a phone kept in a pocket stays signed in. */
    rememberedSessionTtlMs: 30 * 24 * 60 * 60 * 1000,
    /**
     * Age at which the client rotates its session token. Well below either TTL
     * so a rotation failure has many chances to retry before the session dies.
     */
    sessionRefreshAfterMs: 60 * 60 * 1000,

    /**
     * How long the boot screen waits for a stored session to be confirmed when
     * there is no cached identity to paint in the meantime.
     *
     * Short on purpose. It is there to avoid showing the *wrong* screen, not to
     * avoid showing one: past this, sign-in is the honest answer for a session
     * that cannot be reached.
     */
    identityRestoreGraceMs: 2_500,

    /** Failed sign-in attempts allowed per identifier before a cooldown. */
    loginAttemptLimit: 8,
    loginAttemptWindowMs: 15 * 60 * 1000,
    /** Registrations allowed per client in the same window; blunt spam brake. */
    registerAttemptLimit: 5,
  },

  matchmaking: {
    /** Coarse public queue window. Exact Elo expansion lands with persistence. */
    ratingBucketSize: 200,
    privateCodeLength: 4,
    privateCodeAlphabet: '23456789ABCDEFGHJKLMNPQRSTUVWXYZ',
  },

  bot: {
    /** Deliberate mobile-friendly thinking pause between authoritative bot moves. */
    moveIntervalMs: 650,

    /** Chosen when nothing asked for one, and when a public queue falls back. */
    defaultDifficulty: 'medium',

    /**
     * Board evaluation weights, shared by every difficulty.
     *
     * Kept out of the per-difficulty table on purpose: what makes a hard bot
     * hard is how far it looks and how rarely it slips, not a different idea of
     * what a good board is. One opinion about board quality is also one thing
     * to tune when the rules move.
     */
    weights: {
      /** Room to manoeuvre. The single most important term in 2048. */
      emptyCell: 12,
      /** Rows and columns that run one way. Keeps the board from interleaving. */
      monotonicity: 2.4,
      /** Neighbours of similar value, which are the merges available next turn. */
      smoothness: 0.9,
      /** Biggest tile parked in a corner rather than stranded mid-board. */
      maxTileCorner: 26,
      /**
       * Damage is the point of this game, not tidiness: a merge that fires an
       * attack is worth more than the same merge would be in solitaire 2048.
       */
      scoreGain: 1.6,
    },

    /**
     * What separates the four.
     *
     * Three dials, and each one is a different kind of weaker: `searchDepth`
     * limits how far ahead it looks, `blunderChance` makes it occasionally take
     * a legal move it knows is worse, and `moveIntervalMs` decides how much
     * board it gets through in a round.
     *
     * The blunder is what makes an easy bot feel beatable rather than merely
     * slow. A bot that always plays the best move it can see and just plays it
     * rarely is still a bot that never hands you an opening.
     */
    difficulties: {
      easy: { moveIntervalMs: 1_100, blunderChance: 0.35, searchDepth: 1 },
      medium: { moveIntervalMs: 800, blunderChance: 0.15, searchDepth: 1 },
      hard: { moveIntervalMs: 550, blunderChance: 0.04, searchDepth: 2 },
      insane: { moveIntervalMs: 380, blunderChance: 0, searchDepth: 3 },
    },
  },

  audio: {
    /** Music fades out completely before the next scene begins (ms). */
    musicFadeOutMs: 550,
    /** New scene music rises from silence after the previous one has left (ms). */
    musicFadeInMs: 450,
  },

  // --------------------------------------------------------------------------
  // Motion. Read by the render layer only. Durations in seconds because GSAP
  // takes seconds; everything else in this file is milliseconds.
  // --------------------------------------------------------------------------
  motion: {
    /** Entity travelling from one cell to another. */
    moveSec: 0.12,
    /** Collapse/absorb beat, played after the moves land. */
    mergeSec: 0.1,
    /** Scale overshoot on a merge, 1 = no pop. */
    mergePopScale: 1.18,
    /** New entity appearing. */
    spawnSec: 0.1,
    /** Entity leaving the board. */
    removeSec: 0.09,
    /** Glow layer fading in/out. Never animate box-shadow — see docs/RENDERING.md. */
    glowSec: 0.18,
    /** Screen shake on a heavy effect. */
    shakeSec: 0.16,
    shakePx: 6,
    /** Easing curves, by role. */
    ease: {
      move: 'power2.out',
      pop: 'back.out(2.4)',
      fade: 'power1.out',
      shake: 'power2.inOut',
    },
    /** Under `prefers-reduced-motion` every duration is multiplied by this. */
    reducedMotionScale: 0.001,

    /**
     * Chrome motion: view transitions, overlays, toasts and lists. Separate
     * from the board values above because the board is a hot path tuned for
     * feel per-frame, while these are one-shot transitions tuned for reading
     * speed. Both still live here so a motion pass is one file.
     */
    ui: {
      /** Cross-fade of a whole view. */
      viewFadeSec: 0.16,
      /** Directional entrance of a block inside a view. */
      enterSec: 0.28,
      /** Directional exit. Shorter than the entrance: leaving should not wait. */
      exitSec: 0.18,
      /** Gap between consecutive items in a stagger. */
      staggerSec: 0.045,

      /**
       * Ola de letras, para los rótulos que celebran algo: el logo, el récord
       * mundial, lo alto de una tabla. Deliberadamente escasa —si ondulara
       * todo, dejaría de señalar nada—.
       */
      waveSec: 1.6,
      /** Desfase entre letras. Lo que hace que se lea como onda y no como parpadeo. */
      waveStaggerSec: 0.06,
      /** Amplitud relativa a la fuente; se recorre en `transform`, nunca en layout. */
      waveAmplitudeEm: 0.24,

      /** Travel of a directional enter/exit, in CSS px at scale 1080. */
      offsetPx: 28,
      /** Above this many items a list moves as one block instead of staggering. */
      staggerMaxItems: 12,
      ease: {
        enter: 'power2.out',
        exit: 'power2.in',
      },
    },
  },

  // --------------------------------------------------------------------------
  // Input feel.
  // --------------------------------------------------------------------------
  input: {
    /** Minimum pointer travel to count as a swipe, in CSS px at scale 1080. */
    swipeMinDistancePx: 24,
    /** Above this ratio the gesture is ambiguous and is dropped. */
    swipeMaxCrossAxisRatio: 0.75,
    /** A pointer held longer than this is a drag, not a swipe (ms). */
    swipeMaxDurationMs: 700,
    /** Inputs closer together than this are coalesced (ms). Never *dropped*. */
    minIntervalMs: 40,
  },

  // --------------------------------------------------------------------------
  // Rendering budget. The debug overlay flags anything above these.
  // --------------------------------------------------------------------------
  perf: {
    /** Target for our own work inside one frame. */
    frameBudgetMs: 16,
    /** Ceiling during a heavy effect. Also what counts as a genuinely dropped frame. */
    heavyFrameBudgetMs: 32,
    /**
     * One refresh at 60Hz. A frame *interval* cannot be shorter than this, so
     * comparing measured intervals against `frameBudgetMs` would flag every
     * frame of a perfectly smooth 60fps as late. Averages are judged against
     * this; drops are judged against `heavyFrameBudgetMs`.
     */
    vsyncMs: 16.7,
    /**
     * Simultaneous board effects allowed, and therefore the size of the pool:
     * here the two are the same number on purpose. Past this many sparkles at
     * once nobody can pick out one more, so the honest budget is to skip the
     * extra rather than allocate a node — and pay for it in the frame that is
     * already the busiest one.
     */
    effectPoolSize: 32,
    /**
     * Combat effects are serialised by `CombatEffects` itself: starting one
     * finishes whatever was running, so concurrency is one. The spare nodes
     * exist only so the outgoing effect can still be unwinding while the next
     * is placed.
     */
    combatEffectPoolSize: 3,
    /** 16 live tiles + one spawn-before-recycle peak, with three spare slots. */
    entityPoolSize: 20,
  },

  // --------------------------------------------------------------------------
  // PLACEHOLDER ONLY — the throwaway board used to prove the render pipeline.
  // Delete this section the day a real ruleset lands. Nothing outside
  // client/src/game/demo/ may read it.
  // --------------------------------------------------------------------------
  demo: {
    gridCols: 4,
    gridRows: 4,
    entityCount: 5,
  },
} as const;

export type Balance = typeof BALANCE;
